import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  MouseEvent,
} from "react";
import { createRoot, Root } from "react-dom/client";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Stack,
  Fab,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  StickyNote2 as NoteIcon,
} from "@mui/icons-material";
import type { SelectChangeEvent } from "@mui/material/Select";

declare global {
  interface ImportMetaEnv {
    readonly DEV: boolean;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

interface Position {
  x: number;
  y: number;
}

interface Note {
  id: number;
  message: string;
}

interface AlertMessage {
  id: number;
  message: string;
}

interface DraggableHookReturn {
  position: Position;
  handleMouseDown: (e: MouseEvent) => void;
}

const getEstaEmDesenvolvimento = (): boolean => {
  let emDesenvolvimento = false;

  try {
    if (import.meta.env.DEV) {
      emDesenvolvimento = true;
    }
  } catch (e) {
    //
  }

  return emDesenvolvimento;
};

const estaEmDesenvolvimento = getEstaEmDesenvolvimento();

const LogsViewer = ({ logs }: { logs: LogEntry[] }) => {
  const [filterLevel, setFilterLevel] = useState<"all" | "info" | "error">(
    "all"
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [orderBy, setOrderBy] = useState<keyof LogEntry>("timestamp");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const handleFilterChange = (e: SelectChangeEvent<string>) =>
    setFilterLevel(e.target.value as "all" | "info" | "error");
  const handleSearchChange = (e: any) => setSearchTerm(e.target.value);
  const handleRequestSort = (property: keyof LogEntry) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filtered = logs.filter(
    (log) =>
      (filterLevel === "all" || log.level === filterLevel) &&
      log.message.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const sorted = [...filtered].sort((a, b) => {
    if (orderBy === "timestamp") {
      return (
        (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) *
        (order === "asc" ? 1 : -1)
      );
    }
    return a[orderBy].localeCompare(b[orderBy]) * (order === "asc" ? 1 : -1);
  });

  const closeViewer = () => {
    if (logsRoot && logsContainer) {
      logsRoot.unmount();
      logsContainer.remove();
      logsRoot = null;
      logsContainer = null;
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 20,
        bgcolor: "#ffffffcc",
        zIndex: 10000,
        p: 2,
        overflow: "auto",
      }}
    >
      <Paper sx={{ p: 2, maxHeight: "80vh", overflow: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Visualizador de Logs</Typography>
          <IconButton size="small" onClick={closeViewer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <FormControl size="small">
            <InputLabel>Nível</InputLabel>
            <Select
              value={filterLevel}
              label="Nível"
              onChange={handleFilterChange}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="error">Erro</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Buscar mensagem"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "timestamp"}
                    direction={order}
                    onClick={() => handleRequestSort("timestamp")}
                  >
                    Timestamp
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "level"}
                    direction={order}
                    onClick={() => handleRequestSort("level")}
                  >
                    Nível
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "message"}
                    direction={order}
                    onClick={() => handleRequestSort("message")}
                  >
                    Mensagem
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((log, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>
                    {new Date(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>{log.level}</TableCell>
                  <TableCell>{log.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

// Logger para colocar mensagens de erro no window para debug, tanto em produção quanto em desenvolvimento
class Logger {
  private logs: {
    message: string;
    level: "info" | "error";
    timestamp: string;
  }[] = [];
  private readonly MAX_LOGS = 100;

  add(message: string, level: "info" | "error") {
    if (estaEmDesenvolvimento) {
      if (this.logs.length >= this.MAX_LOGS) {
        this.logs.shift(); // Remove o log mais antigo
      }
      this.logs.push({
        message,
        level,
        timestamp: new Date().toISOString(),
      });
    }
  }

  getLogs() {
    return this.logs;
  }
}

// Cria uma instância de logger
const logger = new Logger();

// Função para visualizar os logs
const viewLogs = () => {
  if (estaEmDesenvolvimento) {
    if (!logsContainer) {
      logsContainer = document.createElement("div");
      logsContainer.id = "logs-viewer-container";
      document.body.appendChild(logsContainer);
      logsRoot = createRoot(logsContainer);
    }
    logsRoot?.render(<LogsViewer logs={logger.getLogs()} />);
  }
};

// Adiciona o logger e a função viewLogs ao window para debug
window.sticky_notes_dev_logger = {
  logger,
  viewLogs,
};

declare global {
  interface Window {
    _addStickyNote?: (note: Note) => void;
    _addNoteAlert?: (alert: AlertMessage) => void;
    sticky_notes_dev_logger?: {
      logger: Logger;
      viewLogs: () => void;
    };
  }
}

// Fila de notas pendentes
let pendingNotes: Note[] = [];

// Storage key para as notas
const NOTES_STORAGE_KEY = "stickyNotes";

// Posições padrão
const DEFAULT_CONTAINER_POSITION: Position = { x: 20, y: 20 };
const DEFAULT_FAB_POSITION: Position = { x: 20, y: 20 };

// LocalStorage keys
const CONTAINER_POSITION_KEY = "stickyNoteContainerPosition";
const FAB_POSITION_KEY = "stickyNoteFabPosition";

// Variáveis para o sistema de alertas
let alertContainer: HTMLElement | null = null;
let alertRoot: Root | null = null;
let pendingAlerts: AlertMessage[] = [];
let nextAlertId = Date.now();

// Hook personalizado para drag and drop com localStorage
const useDraggable = (
  initialPosition: Position,
  storageKey: string
): DraggableHookReturn => {
  const [position, setPosition] = useState<Position>(() => {
    const savedPosition = localStorage.getItem(storageKey);
    return savedPosition ? JSON.parse(savedPosition) : initialPosition;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        // Calculate new position
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // Get window dimensions
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Ensure the element stays within window bounds
        const newPosition = {
          x: Math.max(0, Math.min(newX, windowWidth - 300)), // 300 is the width of the container
          y: Math.max(0, Math.min(newY, windowHeight - 50)), // 50 is an approximate height for the minimized state
        };

        setPosition(newPosition);
        localStorage.setItem(storageKey, JSON.stringify(newPosition));
      }
    },
    [isDragging, dragStart, storageKey]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener(
        "mousemove",
        handleMouseMove as unknown as EventListener
      );
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener(
        "mousemove",
        handleMouseMove as unknown as EventListener
      );
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener(
        "mousemove",
        handleMouseMove as unknown as EventListener
      );
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return { position, handleMouseDown };
};

// Variáveis para manter referência do container
let container: HTMLElement | null = null;
let root: Root | null = null;

// Container e root para visualização de logs
let logsContainer: HTMLElement | null = null;
let logsRoot: Root | null = null;

// Componente Container das Notas
const NotesContainer: React.FC = () => {
  try {
    logger.add("Executando NotesContainer", "info");

    const [notes, setNotes] = useState<Note[]>(() => {
      const savedNotes = localStorage.getItem(NOTES_STORAGE_KEY);
      if (!savedNotes) return [];

      // Parse as notas salvas
      const parsedNotes = JSON.parse(savedNotes);

      // Remove duplicatas baseado na mensagem
      const uniqueNotes = parsedNotes.reduce((acc: Note[], current: Note) => {
        const exists = acc.some((note) => note.message === current.message);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      // Se removemos alguma duplicata, atualiza o localStorage
      if (uniqueNotes.length !== parsedNotes.length) {
        localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(uniqueNotes));
      }

      return uniqueNotes;
    });

    const [isMinimized, setIsMinimized] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const containerDrag = useDraggable(
      DEFAULT_CONTAINER_POSITION,
      CONTAINER_POSITION_KEY
    );
    const fabDrag = useDraggable(DEFAULT_FAB_POSITION, FAB_POSITION_KEY);

    const addNote = useCallback((note: Note) => {
      setNotes((prev) => {
        // Verifica se já existe uma nota com o mesmo conteúdo
        const isDuplicate = prev.some(
          (existingNote) => existingNote.message === note.message
        );
        if (isDuplicate) {
          return prev; // Retorna o array anterior sem modificação
        }

        const newNotes = [...prev, note];
        localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(newNotes));
        return newNotes;
      });
    }, []);

    // Processa notas pendentes quando o componente monta
    useEffect(() => {
      if (pendingNotes.length > 0) {
        pendingNotes.forEach((note) => addNote(note));
        pendingNotes = [];
      }
    }, [addNote]);

    // Registra a função addNote globalmente
    useEffect(() => {
      window._addStickyNote = addNote;
      return () => {
        window._addStickyNote = undefined;
      };
    }, [addNote]);

    // Listener para o atalho Ctrl+Shift+O
    useEffect(() => {
      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "o") {
          if (!isVisible) {
            const newContainer = document.createElement("div");
            newContainer.id = "sticky-notes-container";
            document.body.appendChild(newContainer);
            const newRoot = createRoot(newContainer);
            container = newContainer;
            root = newRoot;
            root.render(<NotesContainer />);
          }
        }
      };

      document.addEventListener("keydown", handleKeyPress);
      return () => document.removeEventListener("keydown", handleKeyPress);
    }, [isVisible]);

    // Timer para reexibir as notas
    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (!isVisible) {
        timer = setTimeout(() => {
          const newContainer = document.createElement("div");
          newContainer.id = "sticky-notes-container";
          document.body.appendChild(newContainer);
          const newRoot = createRoot(newContainer);
          container = newContainer;
          root = newRoot;
          root.render(<NotesContainer />);
        }, 120000); // 2 minutos
      }
      return () => clearTimeout(timer);
    }, [isVisible]);

    const removeNote = (id: number) => {
      setNotes((prev) => {
        const newNotes = prev.filter((note) => note.id !== id);
        localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(newNotes));
        return newNotes;
      });
    };

    const handleClose = () => {
      setIsVisible(false);
      window._addStickyNote = undefined;
      if (container) {
        container.remove();
        container = null;
      }
      root = null;
    };

    if (!isVisible) {
      return null;
    }

    return (
      <>
        {isMinimized ? (
          <Box
            sx={{
              position: "fixed",
              left: fabDrag.position.x,
              top: fabDrag.position.y,
              zIndex: 9999,
              cursor: "move",
            }}
            onMouseDown={fabDrag.handleMouseDown}
          >
            <Fab
              color="primary"
              onClick={() => setIsMinimized(false)}
              sx={{ bgcolor: "#fffde7", color: "text.primary" }}
            >
              <NoteIcon />
            </Fab>
          </Box>
        ) : (
          <Box
            sx={{
              position: "fixed",
              left: containerDrag.position.x,
              top: containerDrag.position.y,
              width: 300,
              zIndex: 9999,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                bgcolor: "#fffde7",
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  cursor: "move",
                }}
                onMouseDown={containerDrag.handleMouseDown}
              >
                <Typography variant="subtitle2">
                  Notas ({notes.length})
                </Typography>
                <Box>
                  <IconButton size="small" onClick={() => setIsMinimized(true)}>
                    <ExpandLessIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box
                sx={{
                  maxHeight: "50vh",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                }}
              >
                <Stack spacing={1}>
                  {notes.map((note, index) => (
                    <Paper
                      key={index}
                      elevation={1}
                      sx={{
                        p: 1.5,
                        bgcolor: "#fff",
                        position: "relative",
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          right: 4,
                          top: 4,
                        }}
                        onClick={() => removeNote(note.id)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ pr: 4 }}>{note.message}</Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  color: "text.secondary",
                  fontSize: "0.7rem",
                }}
              >
                Pressione Ctrl+Shift+O para reabrir as notas
              </Typography>
            </Paper>
          </Box>
        )}
      </>
    );
  } catch (error) {
    logger.add("Erro no componente NotesContainer: " + error, "error");
  }
};

// Componente para os Alertas
const AlertsContainer: React.FC = () => {
  try {
    logger.add("Executando AlertsContainer", "info");

    const [alerts, setAlerts] = useState<AlertMessage[]>([]);
    const [hiddenIds, setHiddenIds] = useState<number[]>([]);
    const [isHovered, setIsHovered] = useState(false);
    const prevCount = useRef(alerts.length);

    const addAlert = useCallback((alert: AlertMessage) => {
      setAlerts((prev) =>
        prev.some((a) => a.message === alert.message)
          ? prev
          : [...prev, alert]
      );
    }, []);

    // Registra a função addAlert globalmente
    useEffect(() => {
      window._addNoteAlert = addAlert;
      return () => {
        window._addNoteAlert = undefined;
      };
    }, [addAlert]);

    // Processa alertas pendentes quando o componente monta
    useEffect(() => {
      if (pendingAlerts.length > 0) {
        pendingAlerts.forEach((alert) => addAlert(alert));
        pendingAlerts = [];
      }
    }, [addAlert]);

    // Função para ocultar um alerta e reexibi-lo em 3 minutos
    const hideAlert = useCallback((id: number) => {
      setHiddenIds((prev) => [...prev, id]);
      setTimeout(() => {
        setHiddenIds((prev) => prev.filter((hid) => hid !== id));
      }, 3 * 60 * 1000);
    }, []);

    // Fechar alertas com ESC ao pressionar a tecla Escape
    useEffect(() => {
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          // Ocultar próximo alerta visível
          const visibleAlerts = alerts.filter((a) => !hiddenIds.includes(a.id));
          if (visibleAlerts.length > 0) {
            hideAlert(visibleAlerts[0].id);
          }
        }
      };
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }, [alerts, hiddenIds, hideAlert]);

    // Só remove o container se já havia pelo menos 1 alerta antes
    useEffect(() => {
      if (prevCount.current > 0 && alerts.length === 0 && alertContainer) {
        const handle = setTimeout(() => {
          alertContainer?.remove();
          alertContainer = null;
          alertRoot = null;
        }, 500);
        return () => clearTimeout(handle);
      }
      prevCount.current = alerts.length;
    }, [alerts]);

    // Exibir apenas alertas não ocultos
    const visibleAlerts = alerts.filter((a) => !hiddenIds.includes(a.id));
    if (visibleAlerts.length === 0) {
      return null;
    }
    const alertsToDisplay = isHovered
      ? visibleAlerts
      : visibleAlerts.slice(0, 1);
    const hiddenCount = visibleAlerts.length - alertsToDisplay.length;

    return (
      <Box
        sx={{
          position: "fixed",
          top: 20,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9998,
          width: "auto",
          maxWidth: "90%",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Stack spacing={1}>
          {alertsToDisplay.map((alert) => (
            <Alert
              key={alert.id}
              severity="info"
              onClose={() => hideAlert(alert.id)}
              sx={{
                boxShadow: 2,
                animation: "fadeIn 0.3s ease-in-out",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(-20px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              {alert.message}
            </Alert>
          ))}
        </Stack>
        {!isHovered && hiddenCount > 0 && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, color: "text.secondary" }}
          >
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: 1,
                p: 0.5,
                display: "inline-block",
              }}
            >
              +{hiddenCount} mais
            </Box>
          </Typography>
        )}
      </Box>
    );
  } catch (error) {
    logger.add("Erro no componente AlertsContainer: " + error, "error");
  }
};

interface LogEntry {
  message: string;
  level: "info" | "error";
  timestamp: string;
}

// Função para adicionar uma nova nota
const stickyNote = (message: string): void => {
  try {
    if (!estaEmDesenvolvimento) {
      return;
    }

    logger.add("Executando stickyNote", "info");

    const newNote: Note = {
      id: Date.now(),
      message,
    };

    // Se o container já existe e temos a função registrada, usa ela
    if (window._addStickyNote) {
      window._addStickyNote(newNote);
      return;
    }

    // Se não existe container, cria e adiciona à fila de pendentes
    if (!container) {
      pendingNotes.push(newNote);
      container = document.createElement("div");
      container.id = "sticky-notes-container";
      document.body.appendChild(container);
      root = createRoot(container);
      root.render(<NotesContainer />);
    } else {
      // Se existe container mas não tem função registrada, adiciona à fila
      pendingNotes.push(newNote);
    }
  } catch (error) {
    logger.add("Erro na função stickyNote: " + error, "error");
  }
};

export const noteAlert = (message: string): void => {
  try {
    if (!estaEmDesenvolvimento) {
      return;
    }

    logger.add("Executando noteAlert", "info");

    const newAlert: AlertMessage = {
      id: nextAlertId++,
      message,
    };

    // Se o container já existe e temos a função registrada, usa ela
    if (window._addNoteAlert) {
      window._addNoteAlert(newAlert);
      return;
    }

    // Se não existe container, cria e adiciona à fila de pendentes
    if (!alertContainer) {
      pendingAlerts.push(newAlert);
      alertContainer = document.createElement("div");
      alertContainer.id = "alert-notes-container";
      document.body.appendChild(alertContainer);
      alertRoot = createRoot(alertContainer);
      alertRoot.render(<AlertsContainer />);
    } else {
      // Se existe container mas não tem função registrada, adiciona à fila
      pendingAlerts.push(newAlert);
    }
  } catch (error) {
    logger.add("Erro na função noteAlert: " + error, "error");
  }
};

export default stickyNote;
